import moment from 'moment';
import { IFilterPanelSelection, IFilterPanelValues, ITown, IVehicleScheduleFilters } from 'src/app/models';
import {
  BookingsFilter,
  ExpeditionStatus,
  ExpeditionType,
  FilterField,
  FilterType,
  ServiceType,
  SuggestionPriority,
  SuggestionStatus,
  SuggestionType,
  SuggestionUserResolutionFilter,
  SuggestionVisibility,
  UserStatus,
} from 'src/app/models/enums';
import {
  IAvailabilitiesFilters,
  IBookingFilters,
  IMessagesPanelFilters,
  IServicesFilters,
  ISuggestionsFilters,
  ITripsFilters,
  IUsersFilters,
  parseBookingFiltersFromStorage,
  parseMessagesPanelFiltersFromStorage,
  parseSuggestionsFiltersFromStorage,
  parseTripsFiltersFromStorage,
  parseUsersFiltersFromStorage,
  parseVehiclesFiltersFromStorage,
  serializeMessagesPanelFiltersForStorage,
} from 'src/app/filters';
import { APP } from 'src/app/config/app';
import { StorageService } from 'src/app/services';

export interface IFilterPanelAdapter<T> {
  toFilters(values: IFilterPanelValues): T;
  toSelection(filters: T): IFilterPanelSelection;
  read?(storage: StorageService): Promise<T | null>;
  write?(storage: StorageService, filters: T): Promise<void>;
}

const usersAdapter: IFilterPanelAdapter<IUsersFilters> = {
  toFilters(values) {
    return {
      selectedClients: values.clients.map((client) => client.id!),
      selectedTowns: values.areas.map((area) => area.id!),
      selectedOperators: values.operators.map((operator) => operator.id!),
      selectedStatus: (values.checkboxes[FilterField.USER_STATUS] ?? []) as UserStatus[],
    };
  },
  toSelection(filters) {
    return {
      ids: {
        [FilterField.CLIENTS]: filters.selectedClients,
        [FilterField.AREAS]: filters.selectedTowns,
        [FilterField.OPERATORS]: filters.selectedOperators,
      },
      checked: {
        [FilterField.USER_STATUS]: filters.selectedStatus,
      },
    };
  },
  read: (storage) => storage.getUsersFilters().then((raw) => parseUsersFiltersFromStorage(raw)),
  write: (storage, filters) => storage.setUsersFilters(JSON.stringify(filters)),
};

const bookingsAdapter: IFilterPanelAdapter<IBookingFilters> = {
  toFilters(values) {
    const statuses = values.checkboxes[FilterField.EXPEDITION_STATUS] ?? [];
    return {
      selectedClients: values.clients,
      selectedTowns: values.areas,
      selectedServices: values.services,
      selectedRoutes: values.routes,
      selectedTypes: (values.checkboxes[FilterField.EXPEDITION_MODES] ?? []) as ExpeditionType[],
      selectedServiceTypes: (values.checkboxes[FilterField.SERVICE_TYPES] ?? []) as ServiceType[],
      selectedStatus: statuses.filter(
        (status) => status !== ExpeditionStatus.DELETED && status !== APP.BOOKING_FAULTS_STATUS,
      ) as ExpeditionStatus[],
      cancelled: statuses.includes(ExpeditionStatus.DELETED),
      faults: statuses.includes(APP.BOOKING_FAULTS_STATUS),
      origin: values.origin,
      destination: values.destination,
    };
  },
  toSelection(filters) {
    // the flags win over the stored statuses, which the storage parser fills with the pending default
    const statuses: string[] = filters.cancelled
      ? [ExpeditionStatus.DELETED]
      : filters.faults
        ? [APP.BOOKING_FAULTS_STATUS]
        : [...filters.selectedStatus];
    return {
      ids: {
        [FilterField.CLIENTS]: filters.selectedClients.map((client) => client.id!),
        [FilterField.AREAS]: filters.selectedTowns.map((town) => town.id!),
        SERVICES: filters.selectedServices.map((service) => service.id!),
        ROUTES: filters.selectedRoutes.map((route) => route.id!),
      },
      checked: {
        [FilterField.EXPEDITION_MODES]: filters.selectedTypes,
        [FilterField.SERVICE_TYPES]: filters.selectedServiceTypes,
        [FilterField.EXPEDITION_STATUS]: statuses,
      },
      origin: filters.origin,
      destination: filters.destination,
    };
  },
  read: (storage) => storage.getBookingsFilters().then((raw) => parseBookingFiltersFromStorage(raw)),
  write: (storage, filters) => storage.setBookingsFilters(JSON.stringify(filters)),
};

const tripsAdapter: IFilterPanelAdapter<ITripsFilters> = {
  toFilters(values) {
    return {
      selectedClients: values.clients,
      selectedOperators: values.operators,
      selectedTowns: values.areas,
      selectedServices: values.services,
      selectedRoutes: values.routes,
      selectedDrivers: values.drivers,
      selectedVehicles: values.vehicles,
      selectedTypes: (values.checkboxes[FilterField.EXPEDITION_MODES] ?? []) as ExpeditionType[],
      selectedServiceTypes: (values.checkboxes[FilterField.SERVICE_TYPES] ?? []) as ServiceType[],
      selectedStatus: (values.checkboxes[FilterField.EXPEDITION_STATUS] ?? []) as ExpeditionStatus[],
      selectedAvailabilityId: null,
      from: values.from,
      to: values.to,
    };
  },
  toSelection(filters) {
    return {
      ids: {
        [FilterField.CLIENTS]: filters.selectedClients.map((client) => client.id!),
        [FilterField.OPERATORS]: filters.selectedOperators.map((operator) => operator.id!),
        [FilterField.AREAS]: filters.selectedTowns.map((town) => town.id!),
        SERVICES: filters.selectedServices.map((service) => service.id!),
        ROUTES: filters.selectedRoutes.map((route) => route.id!),
      },
      checked: {
        [FilterField.EXPEDITION_MODES]: filters.selectedTypes,
        [FilterField.SERVICE_TYPES]: filters.selectedServiceTypes,
        [FilterField.EXPEDITION_STATUS]: filters.selectedStatus,
      },
      from: filters.from,
      to: filters.to,
    };
  },
  read: (storage) => storage.getTripsFilters().then((raw) => parseTripsFiltersFromStorage(raw)),
  write: (storage, filters) => storage.setTripsFilters(JSON.stringify(filters)),
};

const availabilitiesAdapter: IFilterPanelAdapter<IAvailabilitiesFilters> = {
  toFilters(values) {
    const bookings = values.checkboxes[FilterField.BOOKINGS] ?? [];
    const withBookings = bookings.includes(BookingsFilter.WITH);
    const withoutBookings = bookings.includes(BookingsFilter.WITHOUT);
    return {
      selectedStatus: (values.checkboxes[FilterField.EXPEDITION_STATUS] ?? []) as ExpeditionStatus[],
      bookings: withBookings === withoutBookings ? undefined : withBookings,
      fromDate: values.from ? moment(values.from).format('YYYY-MM-DD') : undefined,
      toDate: values.to ? moment(values.to).format('YYYY-MM-DD') : undefined,
      startTime: values.startTime,
      endTime: values.endTime,
    };
  },
  toSelection(filters) {
    const bookings =
      filters.bookings === undefined
        ? [BookingsFilter.WITH, BookingsFilter.WITHOUT]
        : [filters.bookings ? BookingsFilter.WITH : BookingsFilter.WITHOUT];
    return {
      ids: {},
      checked: {
        [FilterField.EXPEDITION_STATUS]: filters.selectedStatus,
        [FilterField.BOOKINGS]: bookings,
      },
      from: filters.fromDate ? moment(filters.fromDate, 'YYYY-MM-DD').toDate() : undefined,
      to: filters.toDate ? moment(filters.toDate, 'YYYY-MM-DD').toDate() : undefined,
      startTime: filters.startTime,
      endTime: filters.endTime,
    };
  },
};

const messagesAdapter: IFilterPanelAdapter<IMessagesPanelFilters> = {
  toFilters(values) {
    return {
      selectedClients: values.clients,
      selectedSenders: values.senders,
      selectedReceivers: values.receivers.receivers,
      receiverType: values.receivers.receiverType,
      selectedExpeditionService: values.receivers.expeditionService,
      selectedExpeditionRoute: values.receivers.expeditionRoute,
    };
  },
  toSelection(filters) {
    return {
      ids: {
        [FilterField.CLIENTS]: filters.selectedClients.map((client) => client.id!),
        [FilterField.SENDERS]: filters.selectedSenders.map((sender) => sender.id!),
      },
      receivers: {
        receiverType: filters.receiverType,
        expeditionService: filters.selectedExpeditionService,
        expeditionRoute: filters.selectedExpeditionRoute,
        receivers: filters.selectedReceivers,
      },
    };
  },
  read: (storage) => storage.getMessagesFilters().then((raw) => parseMessagesPanelFiltersFromStorage(raw)),
  write: (storage, filters) => storage.setMessagesFilters(serializeMessagesPanelFiltersForStorage(filters)),
};

const suggestionsAdapter: IFilterPanelAdapter<ISuggestionsFilters> = {
  toFilters(values) {
    return {
      selectedClients: values.clients.map((client) => client.id!),
      selectedTowns: values.areas.map((area) => area.id!),
      selectedServices: values.services,
      selectedTypes: (values.checkboxes[FilterField.SUGGESTION_TYPES] ?? []) as SuggestionType[],
      selectedStatus: (values.checkboxes[FilterField.SUGGESTION_STATUS] ?? []) as SuggestionStatus[],
      selectedPriorities: (values.checkboxes[FilterField.SUGGESTION_PRIORITIES] ?? []) as SuggestionPriority[],
      selectedUserResolutions: (values.checkboxes[FilterField.SUGGESTION_USER_RESOLUTIONS] ??
        []) as SuggestionUserResolutionFilter[],
      selectedVisibilities: (values.checkboxes[FilterField.SUGGESTION_VISIBILITIES] ?? []) as SuggestionVisibility[],
      selectedExpeditions: values.expeditions,
    };
  },
  toSelection(filters) {
    return {
      ids: {
        [FilterField.CLIENTS]: filters.selectedClients,
        [FilterField.AREAS]: filters.selectedTowns,
        SERVICES: filters.selectedServices.map((service) => service.id!),
        EXPEDITIONS: filters.selectedExpeditions.map((expedition) => expedition.id!),
      },
      checked: {
        [FilterField.SUGGESTION_TYPES]: filters.selectedTypes,
        [FilterField.SUGGESTION_STATUS]: filters.selectedStatus,
        [FilterField.SUGGESTION_PRIORITIES]: filters.selectedPriorities,
        [FilterField.SUGGESTION_USER_RESOLUTIONS]: filters.selectedUserResolutions,
        [FilterField.SUGGESTION_VISIBILITIES]: filters.selectedVisibilities,
      },
    };
  },
  read: (storage) => storage.getSuggestionsFilters().then((raw) => parseSuggestionsFiltersFromStorage(raw)),
  write: (storage, filters) => storage.setSuggestionsFilters(JSON.stringify(filters)),
};

const servicesAdapter: IFilterPanelAdapter<IServicesFilters> = {
  toFilters(values) {
    return {
      selectedTowns: values.areas as ITown[],
      selectedClients: values.clients,
      selectedServices: values.services,
      selectedRoutes: values.routes,
      selectedTypes: (values.checkboxes[FilterField.EXPEDITION_MODES] ?? []) as ExpeditionType[],
      selectedServiceTypes: (values.checkboxes[FilterField.SERVICE_TYPES] ?? []) as ServiceType[],
    };
  },
  toSelection(filters) {
    return {
      ids: {
        [FilterField.CLIENTS]: filters.selectedClients.map((client) => client.id!),
        [FilterField.AREAS]: filters.selectedTowns.map((town) => town.id!),
        SERVICES: filters.selectedServices.map((service) => service.id!),
        ROUTES: filters.selectedRoutes.map((route) => route.id!),
      },
      checked: {
        [FilterField.EXPEDITION_MODES]: filters.selectedTypes,
        [FilterField.SERVICE_TYPES]: filters.selectedServiceTypes,
      },
    };
  },
};

const vehiclesAdapter: IFilterPanelAdapter<IVehicleScheduleFilters> = {
  toFilters(values) {
    return {
      clients: values.clients,
      operators: values.operators,
      types: (values.checkboxes[FilterField.EXPEDITION_MODES] ?? []) as ExpeditionType[],
      serviceTypes: (values.checkboxes[FilterField.SERVICE_TYPES] ?? []) as ServiceType[],
      zones: values.areas,
      services: values.services,
      routes: values.routes,
      vehicles: values.vehicles,
      drivers: values.drivers,
      from: values.from,
      to: values.to,
    };
  },
  toSelection(filters) {
    return {
      ids: {
        [FilterField.CLIENTS]: filters.clients.map((client) => client.id!),
        [FilterField.OPERATORS]: filters.operators.map((operator) => operator.id!),
        [FilterField.AREAS]: filters.zones.map((zone) => zone.id!),
        [FilterField.VEHICLES]: filters.vehicles.map((vehicle) => vehicle.id!),
        [FilterField.DRIVERS]: filters.drivers.map((driver) => driver.id!),
        SERVICES: filters.services.map((service) => service.id!),
        ROUTES: filters.routes.map((route) => route.id!),
      },
      checked: {
        [FilterField.EXPEDITION_MODES]: filters.types,
        [FilterField.SERVICE_TYPES]: filters.serviceTypes,
      },
      from: filters.from,
      to: filters.to,
    };
  },
  read: (storage) => storage.getVehiclesFilters().then((raw) => parseVehiclesFiltersFromStorage(raw)),
  write: (storage, filters) => storage.setVehiclesFilters(JSON.stringify(filters)),
};

export const FILTER_PANEL_ADAPTERS: Partial<Record<FilterType, IFilterPanelAdapter<any>>> = {
  [FilterType.USERS]: usersAdapter,
  [FilterType.BOOKINGS]: bookingsAdapter,
  [FilterType.TRIPS]: tripsAdapter,
  [FilterType.AVAILABILITIES]: availabilitiesAdapter,
  [FilterType.MESSAGES]: messagesAdapter,
  [FilterType.SUGGESTIONS]: suggestionsAdapter,
  [FilterType.VEHICLES]: vehiclesAdapter,
  [FilterType.SERVICES]: servicesAdapter,
};
